import elGR from 'antd/es/date-picker/locale/el_GR';
import enUS from 'antd/es/date-picker/locale/en_US';
import antdElGR from 'antd/locale/el_GR';
import antdEnGB from 'antd/locale/en_GB';
import dayjs from 'dayjs';
import i18n from './index';
import {
  DEFAULT_APP_LANGUAGE,
  DEFAULT_DAYJS_LOCALE,
  DEFAULT_NUMBER_FORMATTING_LOCALE,
  SUPPORTED_LANGUAGE_DETAILS,
  type AppLanguage,
} from './supportedLanguages';

type DatePickerLocale = typeof enUS;
type AntdLocale = typeof antdEnGB;

const DATE_PICKER_LOCALE_BY_LANGUAGE: Record<string, DatePickerLocale> = {
  en: enUS,
  el: elGR,
};

const ANTD_LOCALE_BY_LANGUAGE: Record<AppLanguage, AntdLocale> = {
  en: antdEnGB,
  el: antdElGR,
};

// Normalizes any language tag (for example "en-US" or "el_GR") to a base language code.
// Use this before language-based lookups to keep behavior consistent across input formats.
export const normalizeLanguageCode = (language?: string): AppLanguage => {
  if (!language) {
    return DEFAULT_APP_LANGUAGE;
  }

  const normalized = language.trim().toLowerCase().replace('_', '-');

  const baseLanguage = normalized.split('-')[0];

  if (!baseLanguage) {
    return DEFAULT_APP_LANGUAGE;
  }

  return baseLanguage in SUPPORTED_LANGUAGE_DETAILS ? (baseLanguage as AppLanguage) : DEFAULT_APP_LANGUAGE;
};

// Returns the Ant Design DatePicker locale object for the active language.
// Use this for all the date pickers so locale behavior comes from one place.
export const getDatePickerLocaleByLanguage = (language?: string) => {
  const normalizedLanguage = normalizeLanguageCode(language);

  return DATE_PICKER_LOCALE_BY_LANGUAGE[normalizedLanguage] ?? DATE_PICKER_LOCALE_BY_LANGUAGE[DEFAULT_APP_LANGUAGE];
};

// Returns the dayjs locale key for the active language.
// Use this when formatting month/day labels with dayjs.
export const getDayjsLocaleByLanguage = (language?: string) => {
  const normalizedLanguage = normalizeLanguageCode(language);

  return SUPPORTED_LANGUAGE_DETAILS[normalizedLanguage]?.dayjsLocale ?? DEFAULT_DAYJS_LOCALE;
};

// Returns the best supported locale for JavaScript number/currency formatting.
// Use this wherever values are formatted through Intl.NumberFormat.
export const getNumberFormattingLocaleByLanguage = (language?: string) => {
  const normalized = language?.trim().replace('_', '-');

  const normalizedLanguage = normalizeLanguageCode(language);
  const preferredLocale = SUPPORTED_LANGUAGE_DETAILS[normalizedLanguage]?.numberFormattingLocale;

  const candidates = [normalized, preferredLocale, DEFAULT_NUMBER_FORMATTING_LOCALE].filter((value): value is string =>
    Boolean(value)
  );

  const supported = Intl.NumberFormat.supportedLocalesOf(candidates);

  return supported[0] ?? DEFAULT_NUMBER_FORMATTING_LOCALE;
};

// Changes the app language and persists the selection for future visits.
// Use this instead of calling i18n.changeLanguage directly.
export const setAppLanguage = async (language: AppLanguage) => {
  // dayjs first: components re-render during changeLanguage and must format in the new locale.
  dayjs.locale(getDayjsLocaleByLanguage(language));
  await i18n.changeLanguage(language);

  localStorage.setItem('app_language', language);
};

// Returns the Ant Design ConfigProvider locale object for the active language.
// Use this in ConfigProvider so date pickers and table empty states render in the correct language.
export const getAntdLocaleByLanguage = (language?: string) => {
  const normalizedLanguage = normalizeLanguageCode(language);

  return ANTD_LOCALE_BY_LANGUAGE[normalizedLanguage] ?? ANTD_LOCALE_BY_LANGUAGE[DEFAULT_APP_LANGUAGE];
};
